import React, { useEffect, useState } from "react";

import {
  CalendarOutlined, CloseOutlined, DownOutlined, SearchOutlined,
} from "@ant-design/icons";
import {
  Button, Input, Modal, Select,
} from "antd";
import moment from "moment";

import DatePicker from "../../Common/DatePicker/DatePicker";
import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../Common/UIComponents/SelectPopupScrollbar";

import "./PickupFilterModal.css";

const EMPTY_FILTERS = {
  orderDate: "",
  pickupDate: "",
  orderId: "",
  requestedBy: "",
};

const toMoment = (value) => {
  if (!value) return null;
  const parsed = moment(value, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
  return parsed.isValid() ? parsed : null;
};

const toApiDate = (value) => {
  const parsed = toMoment(value);
  return parsed ? parsed.format("YYYY-MM-DD") : "";
};

const toDisplayLabel = (value) => {
  const parsed = toMoment(value);
  if (!parsed) return "";
  return parsed.format("MMM D, YYYY");
};

export default function PickupFilterModal({
  open,
  onCancel,
  onApply,
  filters = EMPTY_FILTERS,
  requestedByOptions = [],
  getReportDates,
}) {
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [orderDateDisplay, setOrderDateDisplay] = useState("");
  const [orderDatePrevDisplay, setOrderDatePrevDisplay] = useState("");
  const [pickupDateDisplay, setPickupDateDisplay] = useState("");
  const [pickupDatePrevDisplay, setPickupDatePrevDisplay] = useState("");

  useEffect(() => {
    if (!open) return;
    const orderLabel = toDisplayLabel(filters.orderDate);
    const pickupLabel = toDisplayLabel(filters.pickupDate);
    setDraft({
      orderDate: toApiDate(filters.orderDate),
      pickupDate: toApiDate(filters.pickupDate),
      orderId: filters.orderId || "",
      requestedBy: filters.requestedBy || "",
    });
    setOrderDateDisplay(orderLabel);
    setOrderDatePrevDisplay(orderLabel);
    setPickupDateDisplay(pickupLabel);
    setPickupDatePrevDisplay(pickupLabel);
  }, [open, filters]);

  const handleOrderDateChange = (dateFrom, dateTo) => {
    if (!dateFrom && !dateTo) {
      setDraft((prev) => ({ ...prev, orderDate: "" }));
      setOrderDateDisplay("");
      setOrderDatePrevDisplay("");
      return;
    }
    const start = moment(dateFrom, "YYYY-MM-DD", true);
    if (!start.isValid()) return;
    const apiDate = start.format("YYYY-MM-DD");
    const label = start.format("MMM D, YYYY");
    setDraft((prev) => ({ ...prev, orderDate: apiDate }));
    setOrderDateDisplay(label);
    setOrderDatePrevDisplay(label);
  };

  const handlePickupDateChange = (dateFrom, dateTo) => {
    if (!dateFrom && !dateTo) {
      setDraft((prev) => ({ ...prev, pickupDate: "" }));
      setPickupDateDisplay("");
      setPickupDatePrevDisplay("");
      return;
    }
    const start = moment(dateFrom, "YYYY-MM-DD", true);
    if (!start.isValid()) return;
    const apiDate = start.format("YYYY-MM-DD");
    const label = start.format("MMM D, YYYY");
    setDraft((prev) => ({ ...prev, pickupDate: apiDate }));
    setPickupDateDisplay(label);
    setPickupDatePrevDisplay(label);
  };

  const handleApply = () => {
    onApply({
      orderDate: draft.orderDate || "",
      pickupDate: draft.pickupDate || "",
      orderId: (draft.orderId || "").trim(),
      requestedBy: (draft.requestedBy || "").trim(),
    });
  };

  const datePickerDisplayProps = {
    displayTextPosition: "right",
    displayClassName: "PickupFilterModal__DateDisplay",
    customCalendarIcon: <CalendarOutlined aria-hidden />,
    emptyPlaceholder: "Pick a Date",
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      width={520}
      className="PickupFilterModal"
      destroyOnClose
    >
      <div className="PickupFilterModal__Header">
        <h3>Filter</h3>
        <button
          type="button"
          className="PickupFilterModal__Close"
          onClick={onCancel}
          aria-label="Close filter"
        >
          <CloseOutlined />
        </button>
      </div>

      <div className="PickupFilterModal__Body">
        <div className="PickupFilterModal__DateRow">
          <div className="PickupFilterModal__Field">
            <label>Order Date</label>
            <div className="PickupFilterModal__DatePickerWrap">
              <DatePicker
                value={orderDateDisplay}
                onChange={setOrderDateDisplay}
                prevValue={orderDatePrevDisplay}
                setPrevValue={setOrderDatePrevDisplay}
                handleSearchDate={handleOrderDateChange}
                getReportDates={getReportDates}
                searchOnClear
                allowClear
                mode="single"
                getPopupContainer={() => document.body}
                popoverClassName="PickupFilterModal__CalendarPopover"
                datePickerDisplayProps={datePickerDisplayProps}
              />
              <DownOutlined className="PickupFilterModal__DateCaret" aria-hidden />
            </div>
          </div>
          <div className="PickupFilterModal__Field">
            <label>Pickup Date</label>
            <div className="PickupFilterModal__DatePickerWrap">
              <DatePicker
                value={pickupDateDisplay}
                onChange={setPickupDateDisplay}
                prevValue={pickupDatePrevDisplay}
                setPrevValue={setPickupDatePrevDisplay}
                handleSearchDate={handlePickupDateChange}
                getReportDates={getReportDates}
                searchOnClear
                allowClear
                mode="single"
                getPopupContainer={() => document.body}
                popoverClassName="PickupFilterModal__CalendarPopover"
                datePickerDisplayProps={datePickerDisplayProps}
              />
              <DownOutlined className="PickupFilterModal__DateCaret" aria-hidden />
            </div>
          </div>
        </div>

        <div className="PickupFilterModal__Field">
          <label htmlFor="pickup-filter-order-id">Order ID</label>
          <Input
            id="pickup-filter-order-id"
            className="PickupFilterModal__SearchInput"
            value={draft.orderId}
            onChange={(e) => setDraft((prev) => ({ ...prev, orderId: e.target.value }))}
            placeholder="Search by Order ID"
            allowClear
            prefix={<SearchOutlined className="PickupFilterModal__SearchIcon" aria-hidden />}
          />
        </div>

        <div className="PickupFilterModal__Field">
          <label htmlFor="pickup-filter-requested-by">Requested by</label>
          <Select
            id="pickup-filter-requested-by"
            className="PickupFilterModal__RequestedByDropdown"
            {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
            classNames={selectPopupClassNames("PickupFilterModal__RequestedByDropdownList")}
            value={draft.requestedBy || undefined}
            onChange={(value) => setDraft((prev) => ({ ...prev, requestedBy: value || "" }))}
            options={requestedByOptions}
            placeholder="Select requested by"
            allowClear
            virtual={false}
            listHeight={256}
            dropdownStyle={{ zIndex: 1200 }}
            getPopupContainer={() => document.body}
            popupRender={(menu) => renderSelectPopupScrollbar(menu, {
              optionCount: (requestedByOptions || []).length,
              className: "PickupFilterModal__RequestedByDropdownScroll",
            })}
          />
        </div>
      </div>

      <div className="PickupFilterModal__Footer">
        <Button
          htmlType="button"
          className="PickupFilterModal__Btn PickupFilterModal__Btn--cancel"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="button"
          className="PickupFilterModal__Btn PickupFilterModal__Btn--apply"
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </Modal>
  );
}
